// src/features/manual/components/MarkdownBody.tsx

import React from 'react';
import { Box } from '@mui/material';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { plainHeading } from '../utils/chapterBlocks';
import { sectionDomId } from '../utils/labels';
import { ManualImage } from './ManualImage';

const CHAPTER_LINK = /^chapter:([a-z0-9]+(?:-[a-z0-9]+)*)(?:#([a-z0-9-]+))?$/;

/** The visible text of rendered Markdown children (strings, arrays and elements). */
const textOf = (node: React.ReactNode): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children);
  return '';
};

interface MarkdownBodyProps {
  markdown: string;
  /** Heading text → anchor: those headings get the id rail links and «Ver en el manual» scroll to. */
  anchors?: Map<string, string>;
}

/**
 * Chapter Markdown in the app's typography. Raw HTML stays off (react-markdown's default), external
 * links open in a new tab, `chapter:<id>[#<anchor>]` links stay inside the app, and images are
 * fetched with the session.
 */
export const MarkdownBody: React.FC<MarkdownBodyProps> = ({ markdown, anchors }) => {
  const heading = (Tag: 'h2' | 'h3' | 'h4'): Components['h2'] =>
    function ManualHeading({ children }) {
      const anchor = anchors?.get(plainHeading(textOf(children)));
      return (
        <Tag id={anchor ? sectionDomId(anchor) : undefined} tabIndex={anchor ? -1 : undefined}>
          {children}
        </Tag>
      );
    };

  const components: Components = {
    h2: heading('h2'),
    h3: heading('h3'),
    h4: heading('h4'),
    img: ({ src, alt }) => (typeof src === 'string' && src ? <ManualImage src={src} alt={alt ?? ''} /> : null),
    a: ({ href, children }) => {
      const chapterLink = CHAPTER_LINK.exec(href ?? '');
      if (chapterLink) {
        return (
          <RouterLink to={`/manual/${chapterLink[1]}`} state={chapterLink[2] ? { anchor: chapterLink[2] } : undefined}>
            {children}
          </RouterLink>
        );
      }
      const external = /^https?:/i.test(href ?? '');
      return (
        <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
          {children}
        </a>
      );
    },
  };

  return (
    <Box
      sx={(theme) => ({
        color: 'text.primary',
        fontSize: '0.9375rem',
        lineHeight: 1.6,
        overflowWrap: 'break-word',
        minWidth: 0,
        '& > :first-of-type': { mt: 0 },
        '& > :last-child': { mb: 0 },
        '& p': { m: '0 0 12px', maxWidth: '72ch' },
        '& ul, & ol': { m: '0 0 12px', pl: '20px', maxWidth: '72ch' },
        '& li': { my: '4px' },
        '& h2': { ...theme.typography.h3, m: '28px 0 8px', scrollMarginTop: '16px' },
        '& h3': { ...theme.typography.h4, m: '22px 0 6px', scrollMarginTop: '16px' },
        '& h4': { ...theme.typography.h5, m: '18px 0 6px', scrollMarginTop: '16px' },
        '& h2:focus, & h3:focus, & h4:focus': { outline: 'none' },
        '& a': { color: theme.custom.text.brand },
        '& a:focus-visible': { outline: 'none', boxShadow: theme.custom.focusRing, borderRadius: theme.custom.radius.small },
        '& strong, & b': { fontWeight: 600 },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          fontSize: '0.8125rem',
          color: theme.custom.text.idMono,
          overflowWrap: 'anywhere',
        },
        '& table': { display: 'block', overflowX: 'auto', borderCollapse: 'collapse', width: '100%', my: 1.5, fontSize: '0.875rem' },
        '& th': {
          textAlign: 'left',
          fontWeight: theme.custom.table.headCell.fontWeight,
          color: theme.custom.table.headCell.color,
          borderBottom: `1px solid ${theme.custom.table.headerBorder}`,
          py: 0.75,
          pr: 1.5,
        },
        '& td': { borderTop: `1px solid ${theme.custom.table.rowBorder}`, py: 0.75, pr: 1.5, verticalAlign: 'top' },
      })}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
        urlTransform={(url) => (CHAPTER_LINK.test(url) ? url : defaultUrlTransform(url))}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownBody;
